import React, {useEffect, useRef, useState} from "react";
import {ApiError} from "../../api";
import {ErrorBanner} from "../../components";
import {getFinanceWalletFlows} from "../../api/admin/finance";
import type {FinanceFlowParty, FinanceMoney, FinanceWalletCash, FinanceWalletFlow, FinanceWalletFlowPage} from "../../api/admin/finance";
import {DayRange, PERIOD_PRESETS, PeriodPicker, PeriodPreset, actionPeriod, plural, presetRange} from "./FinanceActions";
import {financeDay, financeToday, formatFinanceDay, formatFinanceDayTitle, formatFinanceMoment, formatFinanceTime} from "./financeTime";
import {formatFinanceAmount} from "./financeFormat";

// ДДС по кошелькам: переводы, за которые мы платим комиссию, — вывод из
// кабинета Paylord, выплата трейдеру, вывод мерчанту. Один перевод — одна
// строка: сколько ушло, сколько пришло, разница — комиссия. Сверху две
// плитки: комиссии за период по этим трём группам и остатки на кошельках
// системы со сверкой с сетью.

const QUICK: readonly PeriodPreset[] = ["today", "7d", "30d", "month", "prev_month"];
const amount = (value?: FinanceMoney): string => formatFinanceAmount(value);

export const FEE_GROUP_LABELS: Record<string, string> = {paylord: "Paylord", traders: "Трейдеры", merchants: "Мерчанты"};
const CASH_LABELS: Record<string, string> = {paylord: "Кабинеты Paylord", merchants: "Мерчанты", teams: "Команды"};
const TRANSFERS: [string, string, string] = ["перевод", "перевода", "переводов"];

export function shortAddress(address?: string): string { return address ? `${address.slice(0, 6)}…${address.slice(-4)}` : "—"; }

export function partyLabel(party: FinanceFlowParty): string {
    switch (party.kind) {
    case "paylord": return "Paylord";
    case "cabinet": return `Кошелёк ${party.name || shortAddress(party.address)}`;
    case "team": return `Команда ${party.name || shortAddress(party.address)}`;
    case "merchant": return `Мерчант ${party.name || shortAddress(party.address)}`;
    default: return party.address ? `Адрес ${shortAddress(party.address)}` : "—";
    }
}

export function flowTitle(flow: FinanceWalletFlow): string {
    if (flow.kind === "paylord_withdrawal") return "Вывод из Paylord на кошелёк кабинета";
    if (flow.kind === "merchant_withdrawal") return "Вывод мерчанту";
    return "Выплата трейдеру";
}

interface Pill { tone: "warn"; text: string; }

export function flowPills(flow: FinanceWalletFlow): Pill[] {
    if (flow.issue === "receipt_missing") return [{tone: "warn", text: "Поступление не найдено"}];
    if (flow.issue === "fee_unknown") return [{tone: "warn", text: "Нет данных о комиссии"}];
    return [];
}

export function FinanceWalletFlows({token, refresh}: {token: string | null; refresh: number}): JSX.Element {
    const today = financeToday();
    const [range, setRange] = useState<DayRange>(() => presetRange("7d", financeToday()));
    const [page, setPage] = useState<FinanceWalletFlowPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [onlyIssues, setOnlyIssues] = useState(false);
    const seq = useRef(0);

    useEffect(() => {
        if (!token) return;
        const current = ++seq.current;
        let period: {from: string; to: string};
        try { period = actionPeriod(range); } catch (err) { setError(err); setPage(null); setLoading(false); return; }
        setLoading(true); setError(null); setOnlyIssues(false);
        getFinanceWalletFlows(token, period)
            .then((result) => { if (current === seq.current) setPage(result); })
            .catch((err) => { if (current === seq.current) { setError(err); setPage(null); } })
            .finally(() => { if (current === seq.current) setLoading(false); });
    }, [token, range, refresh]);

    useEffect(() => { const current = seq; return () => { ++current.current; }; }, []);

    const sameRange = (other: DayRange): boolean => other.start === range.start && other.end === range.end;
    const items = page ? (onlyIssues ? page.items.filter((flow) => flow.issue) : page.items) : [];
    return <div className="fa">
        <div className="fa-period">
            <div className="fa-presets" role="group" aria-label="Быстрый выбор периода">
                {QUICK.map((key) => { const preset = presetRange(key, today); return <button type="button" key={key} className={sameRange(preset) ? "on" : ""} aria-pressed={sameRange(preset)} onClick={() => setRange(preset)}>{PERIOD_PRESETS.find((p) => p.key === key)?.label}</button>; })}
            </div>
            <PeriodPicker value={range} today={today} onChange={setRange}/>
        </div>
        <ErrorBanner error={error} title={error instanceof ApiError && error.status === 403 ? "Нет доступа к финансовому разделу" : "Не удалось загрузить ДДС по кошелькам"}/>
        {!page && loading && <div className="fa-tiles" aria-busy="true" aria-label="Загрузка итогов"><div className="fa-tile fa-tile-skeleton"/></div>}
        {page && <>
            <section className="fa-balances" aria-label="Итоги за период">
                <div className="fa-tiles">
                    <FeeTile page={page} onShowIssues={() => setOnlyIssues(true)}/>
                    <BalanceTile page={page}/>
                </div>
                <p className="fa-measure">Только {page.token} в сети TRC20.</p>
            </section>
            {onlyIssues && <div className="fa-filterbar" role="status">
                <span>Только переводы без данных о комиссии: {items.length}</span>
                <button type="button" className="fa-linkbtn" onClick={() => setOnlyIssues(false)}>Показать все переводы</button>
            </div>}
            <FlowsFeed items={items} busy={loading}/>
            <div className="fa-foot">
                <span>{items.length > 0 && <>Показано {items.length} {plural(items.length, TRANSFERS)}. </>}Только переводы, за которые платим комиссию. Списано — ушло с кошелька отправителя, получено — пришло получателю, разница — комиссия.</span>
                {page.truncated && <span className="fa-warn-text">Показаны последние переводы периода; итоги посчитаны по всем. Сузьте период, чтобы увидеть остальные.</span>}
            </div>
        </>}
    </div>;
}

/* ─── Плитки ─── */

export function FeeTile({page, onShowIssues}: {page: FinanceWalletFlowPage; onShowIssues?: () => void}): JSX.Element {
    return <div className="fa-tile fa-tile-f" aria-label="Комиссии за период">
        <span className="fa-cap">Комиссии за период</span>
        <strong>{amount(page.fee_total)}<i>{page.token}</i></strong>
        <div className="fa-fees">{page.fees.map((f) => <div key={f.key}>
            <small>{FEE_GROUP_LABELS[f.key] || f.key}</small>
            <b className="mono">{amount(f.fee)}</b>
            <small>{f.count} {plural(f.count, TRANSFERS)}</small>
        </div>)}</div>
        <span className="fa-hint">{page.fee_unknown > 0
            ? <button type="button" className="fa-linkbtn fa-warn-text" onClick={onShowIssues} disabled={!onShowIssues}>Нет данных о комиссии: {page.fee_unknown} {plural(page.fee_unknown, TRANSFERS)} — показать</button>
            : "Комиссия — разница между списанным и полученным."}</span>
    </div>;
}

// chainGap is the balance in the chain at the end minus the one computed from
// the opening and the transfers the system sees; null when either is unknown.
export function chainGap(wallet: FinanceWalletCash): FinanceMoney | null {
    const {closing, chain} = wallet;
    if (!closing || !chain) return null;
    const scale = Math.max(closing.scale, chain.scale);
    const atomic = (m: FinanceMoney): bigint => BigInt(m.atomic) * 10n ** BigInt(scale - m.scale);
    return {atomic: (atomic(chain) - atomic(closing)).toString(), scale, token: chain.token};
}

function sumMoney(values: (FinanceMoney | undefined)[]): FinanceMoney | undefined {
    if (!values.length || values.some((v) => !v)) return undefined;
    const scale = Math.max(...values.map((v) => v!.scale));
    return {atomic: values.reduce((sum, v) => sum + BigInt(v!.atomic) * 10n ** BigInt(scale - v!.scale), 0n).toString(), scale};
}

export function BalanceTile({page}: {page: FinanceWalletFlowPage}): JSX.Element {
    const groups = page.cash || [];
    const wallets = groups.flatMap((g) => g.wallets);
    const matched = wallets.filter((w) => chainGap(w)?.atomic === "0").length;
    const off = wallets.filter((w) => { const gap = chainGap(w); return gap && gap.atomic !== "0"; });
    const opening = sumMoney(groups.map((g) => g.opening));
    const startDay = formatFinanceDay(financeDay(page.from)).slice(0, 5);
    return <div className="fa-tile fa-tile-m" aria-label="Остатки на кошельках">
        <span className="fa-cap">Остатки на кошельках</span>
        <strong>{amount(sumMoney(groups.map((g) => g.chain)))}<i>{page.token}</i></strong>
        {opening && <span className="fa-sub">на {formatFinanceMoment(page.from)} — {amount(opening)}</span>}
        <div className="fa-fees">{groups.map((g) => <div key={g.key}>
            <small>{CASH_LABELS[g.key] || g.key}</small>
            <b className="mono">{amount(g.chain)}</b>
            <small>на {startDay}: {amount(g.opening)}</small>
        </div>)}</div>
        <span className="fa-hint">{matched + off.length === 0
            ? "Сверка с сетью: нет остатка на начало периода."
            : <>Сверка с сетью: сходятся {matched} из {wallets.length}{off.map((w) => <span key={w.party.address || w.party.name} className="fa-warn-text">
                {" · "}{partyLabel(w.party)} {formatFinanceAmount(chainGap(w)!, {sign: true})}
                {w.seen_to && `, переводы видны по ${formatFinanceMoment(w.seen_to)}`}{w.no_history && ", переводы не собираются"}
            </span>)}</>}</span>
    </div>;
}

/* ─── Строки ─── */

function Party({party}: {party: FinanceFlowParty}): JSX.Element {
    return <span className={`fa-party fa-party-${party.kind}`} title={party.address}>{partyLabel(party)}</span>;
}

function FlowsFeed({items, busy}: {items: FinanceWalletFlow[]; busy: boolean}): JSX.Element {
    const body: JSX.Element[] = [];
    let day = "";
    for (const flow of items) {
        const current = financeDay(flow.at);
        if (current !== day) { day = current; body.push(<tr className="fa-day" key={`day:${current}`}><td colSpan={6}>{formatFinanceDayTitle(flow.at)}</td></tr>); }
        body.push(<FlowRow key={flow.id} flow={flow}/>);
    }
    return <div className={`table-wrapper fa-feed fa-flows${busy ? " fa-busy" : ""}`} aria-busy={busy}><table className="finance-flows-table">
        <thead><tr><th>Время</th><th>Откуда → куда</th><th className="fa-num">Списано</th><th className="fa-num">Получено</th><th className="fa-num">Комиссия</th><th>Пометки</th></tr></thead>
        <tbody>{body.length ? body : <tr><td colSpan={6}><div className="empty-state">За период переводов с комиссией нет</div></td></tr>}</tbody>
    </table></div>;
}

function FlowRow({flow}: {flow: FinanceWalletFlow}): JSX.Element {
    return <tr className="fa-row">
        <td data-l="Время" className="mono fa-tm">{formatFinanceTime(flow.at)}</td>
        <td data-l="Откуда → куда">
            <div className="fa-act"><b className="fa-route"><Party party={flow.from}/><span aria-label="на"> → </span><Party party={flow.to}/></b></div>
            <small>{flowTitle(flow)}{flow.book_at && ` · книга ${formatFinanceTime(flow.book_at)}`}</small>
        </td>
        <td data-l="Списано" className="mono fa-num">{amount(flow.sent)}</td>
        <td data-l="Получено" className="mono fa-num">{amount(flow.received)}</td>
        <td data-l="Комиссия" className="fa-num">{flow.fee ? <b className="mono">{amount(flow.fee)}</b> : <span className="fa-muted">нет данных</span>}</td>
        <td data-l="Пометки"><div className="fa-st">
            {flowPills(flow).map((pill) => <span key={pill.text} className={`fa-pill fa-${pill.tone}`}>{pill.text}</span>)}
            {flow.tx_id && <a className="fa-tx" href={`https://tronscan.org/#/transaction/${flow.tx_id}`} target="_blank" rel="noopener noreferrer">Транзакция ↗</a>}
            {!flow.tx_id && flow.book_id && <span className="fa-muted">Строка книги №{flow.book_id}</span>}
        </div></td>
    </tr>;
}
